import React, { useState, useMemo } from 'react';
import {
  Box, Typography, Table, TableBody, TableCell,
  TableHead, TableRow, Paper, makeStyles,
} from '@material-ui/core';
import { PLANNING_DATA, selectTabData, fmtDate, fmtDateTimeSec } from './manufacturingTabData';
import Pagination from '../../logisticsDashboard/components/Pagination';


const useStyles = makeStyles(() => ({
  summaryGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(3, minmax(0, 1fr))',
    gap: '14px 60px',
    padding: '8px 16px',
    fontSize: '0.9rem',
    overflowX: 'hidden',
    width: '100%',
    maxWidth: '100%',
    boxSizing: 'border-box',
    marginBottom: '12px',
  },
  summaryField: {
    display: 'flex',
    flexDirection: 'column',
  },
  summaryLabel: {
    fontWeight: 700,
    color: '#333',
    fontSize: '0.9rem',
    whiteSpace: 'normal',
    wordBreak: 'break-word',
    marginBottom: 2,
  },
  summaryValue: {
    color: '#333',
    fontSize: '0.9rem',
  },
  summaryTimestamp: {
    color: '#555',
    fontSize: '0.85rem',
  },
  tableHeader: {
    '& th': {
      backgroundColor: '#F9F9FC',
      fontWeight: 600,
      fontSize: '0.88rem',
      color: '#333',
      padding: '8px 12px',
      borderBottom: '1px solid #e0e0e0',
      whiteSpace: 'normal',
      wordBreak: 'normal',
      overflowWrap: 'normal',
      position: 'sticky',
      top: 0,
      zIndex: 1,
    },
  },
  scrollArea: {
    width: '100%',
    maxHeight: 440,
    overflowY: 'auto',
    overflowX: 'hidden',
  },
  tableRow: {
    '&:hover': { backgroundColor: '#f5f5f5' },
    '& td': {
      fontSize: '0.9rem',
      padding: '8px 12px',
      borderBottom: '1px solid #eeeeee',
      color: '#444',
      whiteSpace: 'normal',
      wordBreak: 'break-word',
    },
  },
  container: {
    border: '1px solid #e0e0e0',
    borderRadius: '4px',
    margin: '8px 0',
    overflowX: 'hidden',
    width: '100%',
    maxWidth: '100%',
    boxSizing: 'border-box',
  },
}));

const MILESTONE_COLUMNS = ['Activity', 'Target Date', 'Expected Date', 'Actual Date', 'Last Update Timestamp'];

// Planning is driven ENTIRELY by the "Planning" sheet of the batch's Manufacturing
// Collaboration workbook (Excel only — no API merge). Any batch other than the mapped
// ones falls back (via selectTabData) to the representative dataset.
const PlanningTab = ({ loading, batchNumber, pageSize = 10 }) => {
  const classes = useStyles();
  const PAGE_SIZE = pageSize || 10;
  const planning = selectTabData(PLANNING_DATA, batchNumber) || {};
  const headerFields = planning.headerFields || [];
  const milestones = planning.milestones || [];

  const [page, setPage] = useState(1);
  const totalPages = Math.max(1, Math.ceil(milestones.length / PAGE_SIZE));
  const safePage = Math.min(page, totalPages);
  const pageRows = useMemo(
    () => milestones.slice((safePage - 1) * PAGE_SIZE, safePage * PAGE_SIZE),
    [milestones, safePage, PAGE_SIZE]
  );

  if (loading) return <Box style={{ textAlign: 'center', padding: 24 }}>Loading...</Box>;

  return (
    <Box>
      {/* Top 3 Summary Fields */}
      <Box className={classes.summaryGrid}>
        {headerFields.map(row => (
          <Box key={row.label} className={classes.summaryField}>
            <Typography className={classes.summaryLabel}>{row.label}</Typography>
            <Typography className={classes.summaryValue}>{fmtDate(row.value)}</Typography>
            <Typography className={classes.summaryTimestamp}>{fmtDateTimeSec(row.timestamp)}</Typography>
          </Box>
        ))}
      </Box>

      {/* Milestone Table with scrollbar */}
      <Paper className={classes.container} elevation={0}>
        <div className={classes.scrollArea}>
          <Table stickyHeader size="small" style={{ width: '100%', tableLayout: 'auto' }}>
            <TableHead className={classes.tableHeader}>
              <TableRow>
                {MILESTONE_COLUMNS.map(col => (
                  <TableCell key={col}>{col}</TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {milestones.length === 0 ? (
                <TableRow><TableCell colSpan={5} style={{ textAlign: 'center' }}>No data available</TableCell></TableRow>
              ) : pageRows.map((row, idx) => (
                <TableRow key={idx} className={classes.tableRow}>
                  <TableCell>{row.item}</TableCell>
                  <TableCell>{fmtDate(row.targetDate)}</TableCell>
                  <TableCell>{fmtDate(row.expectedDate)}</TableCell>
                  <TableCell>{fmtDate(row.actualDate)}</TableCell>
                  <TableCell>{fmtDateTimeSec(row.lastUpdate)}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      </Paper>

      {milestones.length > PAGE_SIZE && (
        <Pagination page={safePage} totalPages={totalPages} onChange={setPage} />
      )}
    </Box>
  );
};

export default PlanningTab;
